import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Popup, Loader } from "semantic-ui-react";
import classNames from "classnames";
import * as _ from "lodash";
import ExcelExport from "../../../components/ExcelExport/ExcelExport";
import GlobalTableSettings from "../../GlobalTableSettings/GlobalTableSettings";
import ActionMenuButton from "../../../components/ActionMenuButton/ActionMenuButton";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  setEscalatorVersionExtraProperties,
  createEmptyEscalatorVersion
} from "../../../utils/pdApiUtils";
import {
  parseNameToUrl,
  parseUrlToName,
  hasWriteAccess,
  getIndexedValue,
  searchMatchRule
} from "../../../utils/utils";
import NavigationLink from "../../../components/NavigationLink/NavigationLink";
import ModifyEscalatorParameterBindings from "../../../components/ModifyEscalatorParameterBindings/ModifyEscalatorParameterBindings";
import { EditActions, editActions } from "../../../actions/editActions";
import { pdApiActions } from "../../../actions/pdApiActions";
import EscalatorVersions from "./EscalatorVersions";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector
} from "../../../reducers/editReducer";
import { Column, Filter } from "../../../components/Table/Migration/LegacyTypes";

import styles from "../../../components/ActionMenuButton/ActionMenuButton.module.scss";
import exportIcon from "../../../images/export.svg";
import add from "../../../images/circle-plus.svg";
import service from "../../../images/service-24.png";

type StateProps = {
  columns: State.ColumnMap;
  mainState: State.Main;
  pdApi: State.PdApi;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  objectIdentifier: string;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  subMenuRoute: State.EscalatorBindingsSubmenu;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any;
  isLoadingParameters: boolean;
  selectedColumnsName: keyof State.ColumnMap;
};

// Props of the component
export type EscalatorBindingsProps = StateProps & DispatchProps & OwnProps;

type EscalatorBindingsState = {
  activeMenu: State.EscalatorBindingsSubmenu;
  isModalOpen: boolean;
  tableColumnFilters: {
    [key in State.EscalatorBindingsSubmenuWithTables]: Filter[];
  };
};

export class EscalatorBindings extends React.Component<
  EscalatorBindingsProps,
  EscalatorBindingsState
> {
  static subMenus: State.EscalatorBindingsSubmenu[] = [
    "Add Bindings",
    "Remove Bindings",
    "Escalator Versions"
  ];

  state = {
    activeMenu: this.props.match.params.activeMenu
      ? (parseUrlToName(this.props.match.params.activeMenu) as State.EscalatorBindingsSubmenu)
      : "Add Bindings",
    isModalOpen: false,
    tableColumnFilters: {
      "Escalator Versions": []
    }
  };

  componentDidMount(): void {
    const { escalatorParameters, EscalatorVersions, escalatorFaults } = this.props.pdApi;
    const { activeMenu } = this.state;
    //only for bindings page
    if (
      activeMenu !== "Escalator Versions" &&
      window.location.href.includes("bindings") &&
      (!EscalatorVersions || _.isEmpty(EscalatorVersions))
    ) {
      this.props.pdApiActions.fetchEscalatorVersions("Escalator Versions");
    }
    if (escalatorParameters.length === 0) {
      this.props.pdApiActions.fetchEscalatorParameters();
    }
    if (escalatorFaults.length === 0) {
      this.props.pdApiActions.fetchEscalatorFaults();
    }
  }

  componentDidUpdate(): void {
    const parseActiveMenuFromURL = this.props.match.params.activeMenu
      ? (parseUrlToName(this.props.match.params.activeMenu) as State.EscalatorBindingsSubmenu)
      : "Add Bindings";
    if (this.state.activeMenu !== parseActiveMenuFromURL) {
      this.setState({ activeMenu: parseActiveMenuFromURL });
    }
  }

  private setColumnFilters = (
    subMenuRoute: State.EscalatorBindingsSubmenu,
    newFilters: Filter[]
  ): void => {
    const newFilterSet = { ...this.state.tableColumnFilters, [subMenuRoute]: newFilters };
    this.setState(prevState => ({
      ...prevState,
      tableColumnFilters: newFilterSet
    }));
  };

  render(): JSX.Element {
    const {
      pdApi,
      columns,
      subMenuRoute,
      mainState,
      isEditActive,
      objectIdentifier,
      editedObjectId
    } = this.props;
    const { activeUser } = mainState;
    const { activeMenu, isModalOpen } = this.state;
    const { escalatorParameters } = pdApi;
    const filename = activeMenu === "Escalator Versions" ? "escalator_versions" : subMenuRoute;

    // Sorted by versionId. Could be changed if we want to.
    let rows: Api.EscalatorVersion[] = [..._.values(pdApi.EscalatorVersions)].sort(function(
      a: Api.EscalatorVersion,
      b: Api.EscalatorVersion
    ) {
      return b.versionId - a.versionId;
    });
    rows = rows.map(row => setEscalatorVersionExtraProperties(row as Api.EscalatorVersion, pdApi));
    
    const filteredRows = this.filterWithUserSelection(
      this.state.tableColumnFilters[activeMenu as State.EscalatorBindingsSubmenuWithTables],
      rows
    );

    return (
      <div className="table-container">
        <div className="bindingsWrapper">
          <div className="bindingsTabs">
            {EscalatorBindings.subMenus.map(menu => (
              <NavigationLink
                key={menu}
                to={"/escalator/bindings/" + parseNameToUrl(menu)}
                name={menu}
              />
            ))}
          </div>
          {activeMenu === "Escalator Versions" && (
            <div className="actionsMenu">
              <div className="bindingActions">
                {filteredRows.length > 0 && hasWriteAccess(activeUser, "Escalator") ? (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img src={exportIcon} alt="Export to excel" />
                      <ExcelExport
                        rows={filteredRows}
                        columns={columns[activeMenu]}
                        filename={filename}
                      />
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img
                        className={`${styles.icon} ${styles.disableImg}`}
                        src={exportIcon}
                        alt="Export to excel"
                      />
                      <span className={styles.disableText} style={{ textTransform: "capitalize" }}>
                        Export
                      </span>
                    </div>
                  </ActionMenuButton>
                )}

                {hasWriteAccess(activeUser, "Escalator") && !isEditActive ? (
                  <ActionMenuButton
                    id="Add"
                    onClick={() =>
                      this.props.editActions.startEditing(createEmptyEscalatorVersion(), true)
                    }
                  >
                    <div className="actionBtn">
                      <img src={add} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }}>Add {activeMenu}</span>
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton id="Add">
                    <div className="actionBtn">
                      <img src={add} className={styles.disableImg} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }} className={styles.disableText}>
                        Add {activeMenu}
                      </span>
                    </div>
                  </ActionMenuButton>
                )}
              </div>
              {
                <Popup
                  trigger={
                    <div className="tableSetting">
                      <ActionMenuButton>
                        <div className="actionBtn">
                          <img src={service} alt="Table settings" />
                          <span>Table Settings</span>
                        </div>
                      </ActionMenuButton>
                    </div>
                  }
                  wide="very"
                  content={
                    <div onClick={(e) => e.stopPropagation()}>
                      <GlobalTableSettings />
                    </div>
                  }
                  on="click"
                  position="bottom right"
                  className="tableSettingspopup"
                />
              }
            </div>
          )}
        </div>
        <div
          className={classNames({
            bindingsTabPanels: activeMenu === "Add Bindings" || activeMenu === "Remove Bindings",
            bindingsTabTable: activeMenu === "Escalator Versions"
          })}
        >
          {activeMenu !== "Escalator Versions" && this.props.isLoadingParameters ? (
            <Loader inline="centered" content="Loading parameters" active={true} />
          ) : activeMenu === "Add Bindings" ? (
            <ModifyEscalatorParameterBindings
              mode="Add"
              domains={pdApi.domains}
              parameters={escalatorParameters}
              faults={pdApi.escalatorFaults}
              escalatorVersions={pdApi.EscalatorVersions}
              updateEscalatorBindings={(
                operation: string,
                escalatorBulkBindings: Api.EscalatorBulkBindings
              ) => this.props.editActions.updateEscalatorBindings(operation, escalatorBulkBindings)}
              updateEscalatorFaultBindings={(
                operation: string,
                escalatorFaultBulkBindings: Api.EscalatorFaultBulkBindings
              ) => this.props.editActions.updateEscalatorFaultBindings(operation, escalatorFaultBulkBindings)}
              permission={hasWriteAccess(activeUser, "Escalator")}
            />
          ) : activeMenu === "Remove Bindings" ? (
            <ModifyEscalatorParameterBindings
              mode="Remove"
              domains={pdApi.domains}
              parameters={escalatorParameters}
              faults={pdApi.escalatorFaults}
              escalatorVersions={pdApi.EscalatorVersions}
              updateEscalatorBindings={(
                operation: string,
                escalatorBulkBindings: Api.EscalatorBulkBindings
              ) => this.props.editActions.updateEscalatorBindings(operation, escalatorBulkBindings)}
              updateEscalatorFaultBindings={(
                operation: string,
                escalatorFaultBulkBindings: Api.EscalatorFaultBulkBindings
              ) => this.props.editActions.updateEscalatorFaultBindings(operation, escalatorFaultBulkBindings)}
              permission={hasWriteAccess(activeUser, "Escalator")}
            />
          ) : null}
          {activeMenu === "Escalator Versions" && (
            <EscalatorVersions
              {...this.props}
              subMenuRoute="Escalator Versions"
              toggleModal={this.toggleModal}
              rows={rows}
              setColumnFilters={this.setColumnFilters}
            />
          )}
        </div>
        {isEditActive && (
          <ConfirmationModal
            isOpen={isModalOpen}
            size="mini"
            header="Confirm delete"
            content={`Are you sure you want to delete ${activeMenu.replace(/s$/, "")} ${
              objectIdentifier ? objectIdentifier : editedObjectId
            }?`}
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={() => this.props.editActions.delete("EscalatorVersions")}
            onCancel={this.toggleModal}
          />
        )}
      </div>
    );
  }

  private toggleModal = (): void => {
    this.setState(prevState => ({
      isModalOpen: !prevState.isModalOpen
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterWithUserSelection(userFiltering: Filter[] | undefined, rows: any[]): any[] {
    if (!userFiltering || userFiltering.length === 0) {
      return rows;
    }
    const retval = rows.filter(
      row =>
        userFiltering?.every(filter => {
          const column: State.Column | undefined = this.props.columns[
            this.state.activeMenu as State.EscalatorBindingsSubmenuWithTables
          ].find((col: Column) => col.id === filter.id);
          const propertyPath = column?.ownAccessorName || "";
          const propertyPathArray = propertyPath.split(".").slice(1);
          const rowValue = getIndexedValue(propertyPathArray, row);
          const included =
            filter.value === "all"
              ? true
              : !!rowValue && !!filter.value
              ? column?.hasDropdownFiltering
                ? rowValue.toString() === filter.value.toString()
                : searchMatchRule(rowValue.toString(), filter.value.toString())
              : false;
          return included;
        }, this),
      this
    );
    return retval;
  }
}

const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),
  isLoadingParameters: state.loadingStatus.isLoadingEscalatorParameters
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EscalatorBindings);
